// The settings (roadmap 11.P9, P10, P12, P13): the notifications (times, quiet hours, daily limit, what was sent), the
// university (Neptun calendar, the Drive sheet), the Discord channels, the iPhone shortcuts' key, the AI's cost, and the
// state of the server-side secrets (those are set on the server, never here).
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { day, Label, Panel } from '../ui';

const DAYS = ['vasárnap', 'hétfő', 'kedd', 'szerda', 'csütörtök', 'péntek', 'szombat'];
const ST: Record<string, string> = { sent: 'elküldve', quiet: 'csendes óra (reggel összefoglalva)', capped: 'napi limit felett', none: 'nem ért célba', duplicate: 'ismétlés' };

export function Prefs() {
  const [s, setS] = useState<any>(null);
  const [msg, setMsg] = useState('');
  const load = useCallback(() => api('/api/settings').then(setS).catch((e) => setMsg(e.message)), []);
  useEffect(() => { load(); }, [load]);
  if (!s) return <p className="muted">{msg || 'Beállítások betöltése…'}</p>;
  return <><Notify s={s} reload={load} /><Uni s={s} reload={load} /><Discord s={s} reload={load} /><Ingest s={s} /><Ai s={s} /><Secrets s={s} /></>;
}

function Notify({ s, reload }: { s: any; reload: () => void }) {
  const [f, setF] = useState(s.notify);
  const [msg, setMsg] = useState('');
  const set = (k: string, v: any) => setF((x: any) => ({ ...x, [k]: v }));
  const save = () => api('/api/settings/notify', f, { method: 'PUT' }).then(() => { setMsg('Elmentve.'); reload(); }).catch((e) => setMsg(e.message));
  return (
    <Panel c="mint"><Label c="mint">Értesítések – mikor és mennyit</Label>
      <form className="money-form" onSubmit={(e) => { e.preventDefault(); save(); }}>
        <div className="j-row">
          <label>Csendes órák kezdete<input className="j-input" type="number" min={0} max={23} value={f.quietFrom} onChange={(e) => set('quietFrom', Number(e.target.value))} /></label>
          <label>vége<input className="j-input" type="number" min={0} max={23} value={f.quietTo} onChange={(e) => set('quietTo', Number(e.target.value))} /></label>
          <label>Sürgős naponta legfeljebb<input className="j-input" type="number" min={0} max={30} value={f.maxUrgentPerDay} onChange={(e) => set('maxUrgentPerDay', Number(e.target.value))} /></label>
        </div>
        <div className="j-row">
          <label>Reggeli<input className="j-input" type="time" value={f.morning} onChange={(e) => set('morning', e.target.value)} /></label>
          <label>Esti<input className="j-input" type="time" value={f.evening} onChange={(e) => set('evening', e.target.value)} /></label>
          <label>Napi átnézés<input className="j-input" type="time" value={f.review} onChange={(e) => set('review', e.target.value)} /></label>
        </div>
        <div className="j-row">
          <label>Heti összefoglaló<select className="j-input" value={f.weeklyDay} onChange={(e) => set('weeklyDay', Number(e.target.value))}>{DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}</select></label>
          <label>időpont<input className="j-input" type="time" value={f.weekly} onChange={(e) => set('weekly', e.target.value)} /></label>
        </div>
        <button className="j-btn j-btn--primary">Mentés</button>{msg && <span className="small"> {msg}</span>}
      </form>
      <details className="sources"><summary>Az utóbbi értesítések ({s.notifications.length})</summary>
        {s.notifications.map((n: any, i: number) => (
          <div key={i} className="j-slot"><div className="t">{day(n.at)} {new Date(n.at).toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })}</div>
            <div><div>{n.title}</div><div className="w">{n.body ? `${String(n.body).slice(0, 140)} · ` : ''}{ST[n.status] ?? n.status}</div></div></div>
        ))}
      </details>
    </Panel>
  );
}

function Uni({ s, reload }: { s: any; reload: () => void }) {
  const [f, setF] = useState({ ics: s.uni.ics ?? '', sheet: s.uni.sheet ?? '', startYear: s.uni.startYear ?? '' });
  const [msg, setMsg] = useState('');
  const save = () => api('/api/settings/uni', { ...f, startYear: f.startYear ? Number(f.startYear) : null }, { method: 'PUT' }).then(() => { setMsg('Elmentve.'); reload(); }).catch((e) => setMsg(e.message));
  return (
    <Panel c="violet"><Label c="violet">Egyetem</Label>
      <form className="money-form" onSubmit={(e) => { e.preventDefault(); save(); }}>
        <label>Neptun-naptár (ICS) linkje<input className="j-input" value={f.ics} onChange={(e) => setF({ ...f, ics: e.target.value })} placeholder="https://neptun…/…ics" /></label>
        <label>A tanulmányi Excel (Tanulmányok.xlsx a Google Drive-on) linkje<input className="j-input" value={f.sheet} onChange={(e) => setF({ ...f, sheet: e.target.value })} placeholder="https://drive.google.com/file/d/…" />
          <span className="small muted">Oszd meg Szerkesztőként a Jarvis Google-fiókjával (az Egyetem lencse Források részén látod); oda-vissza szinkronizálom.</span></label>
        <label>Kezdés éve<input className="j-input" type="number" value={f.startYear} onChange={(e) => setF({ ...f, startYear: e.target.value })} /></label>
        <button className="j-btn j-btn--primary">Mentés</button>{msg && <span className="small"> {msg}</span>}
      </form>
    </Panel>
  );
}

function Discord({ s, reload }: { s: any; reload: () => void }) {
  const [chs, setChs] = useState<Set<string>>(new Set(s.discord.channels));
  const [me, setMe] = useState(s.discord.me ?? '');
  const [teachers, setTeachers] = useState(s.discord.teachers ?? '');
  const [msg, setMsg] = useState('');
  const flip = (id: string) => setChs((x) => { const n = new Set(x); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const save = () => api('/api/settings/discord', { channels: [...chs], me, teachers }, { method: 'PUT' })
    .then((r: any) => { setMsg(r.backfill ? `Elmentve; ${r.backfill} új csatorna elmúlt 14 napja most jön be.` : 'Elmentve.'); reload(); }).catch((e) => setMsg(e.message));
  return (
    <Panel c="cyan"><Label c="cyan">Discord</Label>
      <div className="j-row">
        <label>A te neved a Discordon<input className="j-input" value={me} onChange={(e) => setMe(e.target.value)} /></label>
        <label>Tanárok (vesszővel)<input className="j-input" value={teachers} onChange={(e) => setTeachers(e.target.value)} /></label>
      </div>
      {s.discord.servers.map((g: any) => (
        <details key={g.id} className="sources" open={s.discord.servers.length === 1}><summary>{g.name || 'szerver'} · {g.channels.filter((c: any) => chs.has(c.id)).length} figyelt</summary>
          {g.channels.map((c: any) => (
            <label key={c.id} className="check-row"><input type="checkbox" checked={chs.has(c.id)} onChange={() => flip(c.id)} /> {c.category ? <span className="muted small">{c.category} › </span> : null}#{c.name}</label>
          ))}
        </details>
      ))}
      {!s.discord.servers.length && <p className="small muted">A bot még nem látott szervert (a token a szerveren: <code>sudo jarvis2-secret discord</code>).</p>}
      <button className="j-btn j-btn--primary" onClick={save}>Mentés</button>{msg && <span className="small"> {msg}</span>}
    </Panel>
  );
}

function Ingest({ s }: { s: any }) {
  const [tok, setTok] = useState<string | null>(null);
  const [sure, setSure] = useState(false);
  const make = () => api<{ token: string }>('/api/settings/ingest-token', {}).then((r) => { setTok(r.token); setSure(false); });
  return (
    <Panel c="amber"><Label c="amber">iPhone-parancsok kulcsa</Label>
      <p className="small">{s.ingest.set ? 'Van beállított kulcs (a banki SMS, az Apple Pay, a gyors rögzítés és a Siri-kérdés ezzel jön).' : 'Még nincs kulcs.'}</p>
      {tok ? <>
        <p className="small"><b>Csak most látszik.</b> A Parancsok appban minden Jarvis-parancs „Tartalom lekérése URL-ről” lépésénél a fejlécben: <code>Authorization</code> = <code>Bearer {tok}</code></p>
        <button className="j-btn" onClick={() => navigator.clipboard?.writeText(`Bearer ${tok}`)}>Másolás</button>
      </> : sure ? <div className="j-row"><span className="small">A régi kulcs azonnal megszűnik – minden parancsot át kell írni.</span><button className="j-btn j-btn--primary" onClick={make}>Új kulcs</button><button className="j-btn linkish" onClick={() => setSure(false)}>Mégse</button></div>
        : <button className="j-btn" onClick={() => setSure(true)}>{s.ingest.set ? 'Új kulcs készítése' : 'Kulcs készítése'}</button>}
    </Panel>
  );
}

function Ai({ s }: { s: any }) {
  const byDay = new Map<string, number>();
  for (const r of s.ai.days) byDay.set(r.day, (byDay.get(r.day) ?? 0) + Number(r.usd));
  return (
    <Panel c="coral"><Label c="coral">AI-költség</Label>
      <p className="small">Ma {s.ai.today.toFixed(2)} $ a napi {s.ai.limit} $ keretből.</p>
      <p className="small muted">{[...byDay].map(([d, v]) => `${d.slice(5)}: ${v.toFixed(2)} $`).join(' · ')}</p>
      <details className="sources"><summary>Mire ment</summary>{s.ai.days.map((r: any, i: number) => <div key={i} className="small">{r.day.slice(5)} · {r.purpose} · {r.usd} $</div>)}</details>
    </Panel>
  );
}

function Secrets({ s }: { s: any }) {
  const row = (name: string, ok: boolean, cmd: string) => <div className="row-split small"><span>{name}</span><span>{ok ? '✓ beállítva' : <code>{cmd}</code>}</span></div>;
  return (
    <Panel c="violet"><Label c="violet">Jelszavak, tokenek (a szerveren)</Label>
      <p className="small muted">Ezek soha nem mennek át az appon, az adatbázison vagy az AI-n. Csere a szerveren: <code>sudo jarvis2-secret &lt;név&gt;</code>.</p>
      {row('Neptun', s.secrets.neptun, 'sudo jarvis2-secret neptun')}
      {row('Discord-bot', s.secrets.discord, 'sudo jarvis2-secret discord')}
      {row('Gmail', s.secrets.gmail, 'sudo jarvis2-secret mail-gmail')}
      {row('jarvis@ggyt.hu', s.secrets.jarvis, 'sudo jarvis2-secret mail-jarvis')}
    </Panel>
  );
}
